<script lang="ts" setup>
import type { ResolvedMonospacePage, WorkspaceWrappedItem } from '#layers/data-model/public';
import type { SidebarAction, SidebarItem } from '#layers/ui/public';
import { useOverlay } from '@nuxt/ui/composables';
import { useFuse } from '@vueuse/integrations/useFuse';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHead } from '#app/composables/head';
import { useRoute, useRouter } from '#app/composables/router';
import { useAppConfig } from '#app/config';
import { usePermissionsStore } from '#layers/access/public';
import { customSpacesFlag, isRouteParamTrue, toSortedArray } from '#layers/base/public';
import { useTranslationUtils } from '#layers/i18n/public';
import { useWorkspaceEntityStore } from '#layers/runtime/public';
import { getSpacePageRouteParams, SpacePageSectionCreate, SpacePageSectionEdit, usePageActions, useSpaceEdit } from '#layers/spaces/public';
import WorkspaceChrome from '#layers/ui-workspace/components/WorkspaceChrome.vue';
import { ErrorBoundary, showConfirmationDialog } from '#layers/ui/public';
import SpaceBreadcrumb from './SpaceBreadcrumb.vue';

const props = defineProps<{
	id: string;
}>();

const { t } = useI18n();
const appConfig = useAppConfig();
const { openSpaceEdit, deleteSpace } = useSpaceEdit();
const store = useWorkspaceEntityStore();
const { hasAnyAccess } = usePermissionsStore();
const { translateString } = useTranslationUtils();
const { createPage: _createPage, editPage, confirmDeletePage } = usePageActions();

const { data: space } = await store.MonospaceSpace.query((q) => q.first({
	key: props.id,
	include: { pages: { include: { block: {} } }, block: {}, sections: {} },
	fetchPolicy: 'cache-and-fetch',
}));

const router = useRouter();
const route = useRoute();

// Also gates a hand-typed `?buildSpace=true`, so a deep-linked space stays readable without
// exposing the space builder while custom spaces are hidden.
const buildSpace = computed(() => customSpacesFlag.value && isRouteParamTrue(route.query.buildSpace));

// router.hasRoute is not reactive
const forceLinkRefresh = ref(0);

async function createPage(sectionId?: string) {
	if (!space.value) return;

	_createPage(space.value.id, sectionId);
}

useHead(() => ({
	title: translateString(space.value?.title),
}));

function onNeedsRouteUpdate() {
	forceLinkRefresh.value++;
}

// Page tree

function isPageAccessible(page: ResolvedMonospacePage) {
	if (!page.collectionId) return true;
	return hasAnyAccess(page.collectionId);
}

const spacePages = computed(() => space.value?.pages.filter((p) => !p.isHidden) ?? []);

const search = ref('');
const searchEnabled = computed(() => spacePages.value.length >= 15);

const { results } = useFuse(search, spacePages, {
	fuseOptions: {
		keys: [
			'title',
		],
		includeMatches: true,
		threshold: 0.5,
	},
});

function clearSearch() {
	search.value = '';
}

function mapPageToItem(page: ResolvedMonospacePage) {
	return {
		id: page.id,
		label: page.title,
		icon: page.icon || 'lucide:file',
		iconColor: page.iconColor ?? undefined,
		to: router.hasRoute(page.id)
			? {
					name: page.id,
					params: getSpacePageRouteParams(page),
				}
			: undefined,
		active: route.matched.some((r) => r.name === page.id),
		actions: getPageActions(page),
	};
}

function getPageActions(page: ResolvedMonospacePage): SidebarAction[] {
	return [
		{
			id: 'edit',
			icon: appConfig.ui.icons.edit,
			label: t('page.edit_page'),
			onSelect: () => {
				editPage(page);
			},
		},
		{
			id: 'delete',
			icon: appConfig.ui.icons.trash,
			label: t('page.delete_page_label'),
			color: 'error',
			onSelect: () => {
				confirmDeletePage(page);
			},
		},
	];
}

const sidebarItems = computed(() => {
	const result: Array<{ item: SidebarItem; sort: number }> = [];

	if (space.value) {
		const isSearching = search.value && searchEnabled.value;

		if (isSearching) {
			return results.value.map((r) => ({
				...mapPageToItem(r.item),
				searchMatches: r.matches,
				searchMatchKey: 'title',
			} satisfies SidebarItem));
		}

		const pages = spacePages.value;

		// Pages with no section (root pages)
		result.push(...pages.filter((p) => !p.section && !p.isHidden && isPageAccessible(p)).map((p) => ({
			sort: p.sort,
			item: mapPageToItem(p),
		})));

		// Sections
		for (const section of space.value.sections) {
			result.push({
				item: {
					id: section.id,
					label: section.title,
					section: true,
					children: toSortedArray(section.pages.filter((p) => !p.isHidden && isPageAccessible(p))).map((p) => mapPageToItem(p)),
					actions: [
						{
							label: t('space.add_new_page'),
							onSelect: () => {
								createPage(section.id);
							},
						},
						{
							label: t('page_section.rename_section'),
							onSelect: () => {
								editSection(section.id);
							},
						},
						{
							label: t('page_section.remove_section'),
							color: 'error',
							onSelect: () => {
								removeSection(section);
							},
						},
					] satisfies SidebarAction[],
				},
				sort: section.sort,
			});
		}
	}

	return toSortedArray(result).map(({ item }) => item);
});

const headerActions = computed(() =>
	[
		{
			label: t('space.add_new_page'),
			icon: appConfig.ui.icons.plus,
			onSelect: () => {
				createPage();
			},
		},
		{
			label: t('space.add_new_section'),
			icon: 'lucide:folder-plus',
			onSelect: () => {
				createSection();
			},
		},
		...(customSpacesFlag.value
			? [
				{
					type: 'separator',
				},
				{
					label: t('space.edit_space'),
					icon: 'lucide:square-pen',
					onSelect: () => {
						openSpaceEdit();
					},
				},
				{
					label: t('space.delete_space_label'),
					icon: appConfig.ui.icons.trash,
					color: 'error',
					onSelect: () => {
						deleteSpace(space.value!);
					},
				},
			] satisfies SidebarAction[]
			: []),
	] satisfies SidebarAction[],
);

// Page section

const overlay = useOverlay();

function createSection() {
	if (!space.value) return;
	const modal = overlay.create(SpacePageSectionCreate, {
		props: {
			spaceId: space.value.id,
		},
	});
	modal.open();
}

function editSection(sectionId: string) {
	if (!space.value) return;
	const modal = overlay.create(SpacePageSectionEdit, {
		props: {
			id: sectionId,
		},
	});
	modal.open();
}

async function removeSection(section: WorkspaceWrappedItem<'MonospacePageSection'>) {
	if (!space.value) return;

	if (await showConfirmationDialog({
		props: {
			title: t('page_section.remove_section_confirm.title', {
				title: section.title,
			}),
			description: t('page_section.remove_section_confirm.description'),
			confirm: {
				label: t('common.remove'),
				color: 'error',
			},
		},
	})) {
		// Clear all pages of the section in the cache
		for (const page of section.pages) {
			store.MonospacePage.clearItem(page.id);

			if (route.name === page.id) {
				router.push({
					name: space.value.id,
				});
			}
		}

		await section.$delete();
	}
}
</script>

<template>
	<WorkspaceChrome>
		<template #header-title>
			<SpaceBreadcrumb />
		</template>

		<template #sidebar>
			<LazySpaceBuilder
				v-if="buildSpace && space?.block?.id"
				:space
				:block-id="space.block.id"
				@needs-route-update="onNeedsRouteUpdate()"
			/>

			<DSidebar
				v-else-if="space"
				:key="forceLinkRefresh"
				:title="translateString(space.title)"
				:header-actions
				:items="sidebarItems"
				:empty="{
					title: t('space.empty'),
					description: t('space.empty_description'),
					actionButtonLabel: t('space.add_new_page'),
					onAction: () => createPage(),
				}"
			>
				<template #content-before>
					<div
						v-if="searchEnabled"
						class="mbe-2 px-3"
					>
						<DInput
							v-model="search"
							:placeholder="$t('common.search')"
							size="sm"
							class="w-full"
							:icon="appConfig.ui.icons.search"
						>
							<template v-if="search.length > 0" #trailing>
								<DButton
									icon="lucide:x"
									size="xs"
									color="neutral"
									variant="link"
									:aria-label="$t('common.clear')"
									@click="clearSearch"
								/>
							</template>
						</DInput>
					</div>
				</template>

				<template #content-bottom>
					<BlockRender
						v-if="space.block?.id && (sidebarItems.length > 0 || !space.onEmptyMoveBlockToPage)"
						:block="space.block.id"
						:context="{
							spaceIsEmpty: sidebarItems.length === 0,
						}"
					/>
				</template>
			</DSidebar>
		</template>

		<template v-if="sidebarItems.length === 0 && space">
			<DEmpty
				fill
				action-button-primary
				:title="$t('space.empty')"
				icon="lucide:file"
				:description="$t('space.empty_description')"
				:action-button-label="$t('space.add_new_page')"
				@action="createPage()"
			/>
			<BlockRender
				v-if="space.block?.id && space.onEmptyMoveBlockToPage"
				:block="space.block.id"
				:context="{
					spaceIsEmpty: true,
				}"
			/>
		</template>
		<ErrorBoundary v-else>
			<NuxtPage />
		</ErrorBoundary>

		<div
			v-if="buildSpace"
			id="space-builder-pane"
			class="absolute inset-0 z-9 bg-neutral-800/60"
		/>
	</WorkspaceChrome>
</template>
